import React, { ReactNode, useCallback } from 'react'
import { nanoid } from 'nanoid'
import { useSnackbar, OptionsObject, SnackbarKey } from 'notistack'
import { Alert, AlertProps, Box, Button, IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'

export interface NotistackSnackbarOptions extends Omit<OptionsObject, 'variant' | 'action'> {
  action?: string | ReactNode | ((key: SnackbarKey) => ReactNode)
  onAction?: () => void
  severity?: 'success' | 'info' | 'warning' | 'error'
  variant?: AlertProps['variant']
  elementName?: string
}

const ActionComponent = ({
  snackbarKey,
  action,
  onAction,
  severity,
  elementName,
  onClose
}: {
  snackbarKey: SnackbarKey
  action: string | ReactNode | ((key: SnackbarKey) => ReactNode)
  severity: NotistackSnackbarOptions['severity']
  elementName?: string
  onAction?: () => void
  onClose: () => void
}) => {
  let actionNode
  const handleAction = useCallback(() => {
    onAction?.()
    onClose()
  }, [onAction, onClose])
  if (typeof action !== 'undefined') {
    if (typeof action === 'function') {
      actionNode = action(snackbarKey)
    }
    if (typeof action === 'string') {
      actionNode = (
        <Button variant="outlined" color={severity} onClick={handleAction} size="small">
          {action}
        </Button>
      )
    }
  }
  return (
    <>
      {action && <Box data-element={`${elementName}-action-button`}>{actionNode}</Box>}
      {(action || onAction) && (
        <IconButton data-element={`${elementName}-close-button`} size="small" onClick={() => onClose()}>
          <Close sx={{ fontSize: 'inherit' }} />
        </IconButton>
      )}
    </>
  )
}

const useNotistackSnackbar = () => {
  const { enqueueSnackbar: originalEnqueueSnackbar, closeSnackbar } = useSnackbar()

  const enqueueSnackbar = useCallback(
    (message: string | ReactNode, props: NotistackSnackbarOptions) => {
      const {
        persist = false,
        key = nanoid(),
        action,
        onAction,
        severity = 'success',
        elementName = 'alert',
        variant,
        anchorOrigin = {
          vertical: 'bottom',
          horizontal: 'left'
        },
        onClick,
        ...rest
      } = props

      originalEnqueueSnackbar(
        <Alert
          severity={severity}
          variant={variant}
          sx={{ width: '100%' }}
          data-element={elementName}
          action={
            <ActionComponent
              snackbarKey={key}
              action={action}
              elementName={elementName}
              onAction={onAction}
              severity={severity}
              onClose={() => closeSnackbar(key)}
            />
          }
        >
          <span data-element={`${elementName}-message`}>{message}</span>
        </Alert>,
        {
          key,
          persist,
          anchorOrigin,
          ...rest
        }
      )
    },
    [closeSnackbar, originalEnqueueSnackbar]
  )

  return {
    enqueueSnackbar,
    closeSnackbar
  }
}

export default useNotistackSnackbar
